import { openDB, IDBPDatabase } from 'idb';
import { RENDERER_VERSION } from './funnelMapSimplePreviewVersion';

const DB_NAME = 'funnel-previews';
const DB_VERSION = 1;
const PREVIEWS_STORE = 'previews';
const META_STORE = 'meta';
const RENDERER_VERSION_KEY = 'renderer-version';

interface PreviewDB {
  previews: {
    key: string;
    value: Blob;
  };
  meta: {
    key: string;
    value: number;
  };
}

let dbPromise: Promise<IDBPDatabase<PreviewDB>> | null = null;

const getDB = (): Promise<IDBPDatabase<PreviewDB>> => {
  if (!dbPromise) {
    dbPromise = openDB<PreviewDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(PREVIEWS_STORE)) {
          db.createObjectStore(PREVIEWS_STORE);
        }
        if (!db.objectStoreNames.contains(META_STORE)) {
          db.createObjectStore(META_STORE);
        }
      },
    });
  }
  return dbPromise;
};

export const getStoredRendererVersion = async (): Promise<number | undefined> => {
  try {
    const db = await getDB();
    return db.get(META_STORE, RENDERER_VERSION_KEY);
  } catch {
    return undefined;
  }
};

export const setStoredRendererVersion = async (version: number): Promise<void> => {
  try {
    const db = await getDB();
    await db.put(META_STORE, version, RENDERER_VERSION_KEY);
  } catch {
    // IndexedDB unavailable
  }
};

export const getAllPreviews = async (): Promise<Array<{ key: string; value: Blob }>> => {
  try {
    const db = await getDB();
    const keys = await db.getAllKeys(PREVIEWS_STORE);
    const values = await db.getAll(PREVIEWS_STORE);
    return keys.map((key, i) => ({ key: key as string, value: values[i] }));
  } catch {
    return [];
  }
};

export const getPreviewFromDB = async (key: string): Promise<Blob | undefined> => {
  try {
    const db = await getDB();
    return db.get(PREVIEWS_STORE, key);
  } catch {
    return undefined;
  }
};

export const setPreviewInDB = async (key: string, blob: Blob): Promise<void> => {
  try {
    const db = await getDB();
    await db.put(PREVIEWS_STORE, blob, key);
  } catch {
    // IndexedDB unavailable or quota exceeded
  }
};

export const deletePreviewFromDB = async (key: string): Promise<void> => {
  try {
    const db = await getDB();
    await db.delete(PREVIEWS_STORE, key);
  } catch {
    // IndexedDB unavailable
  }
};

export const clearAllPreviews = async (): Promise<void> => {
  try {
    const db = await getDB();
    await db.clear(PREVIEWS_STORE);
  } catch {
    // IndexedDB unavailable
  }
};

export const initializePreviewStorage = async (): Promise<Map<string, Blob>> => {
  const memoryCache = new Map<string, Blob>();

  try {
    const storedVersion = await getStoredRendererVersion();

    if (storedVersion !== RENDERER_VERSION) {
      await clearAllPreviews();
      await setStoredRendererVersion(RENDERER_VERSION);
      return memoryCache;
    }

    const previews = await getAllPreviews();
    previews.forEach(({ key, value }) => {
      memoryCache.set(key, value);
    });
  } catch {
    // IndexedDB unavailable, return empty cache
  }

  return memoryCache;
};
